import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import AcxVirtualizedSelectMulti from "components/UI/Select/AcxVirtualizedSelectMulti";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;

export const PrimaryTopicsFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const { availableOptions: availablePrimaryTopics } = useRestrictedOptions({
        options: valuesStore.primaryTopics,
        getOptionValue: (topic) => topic.topicNumber,
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const allowedModes =
        props.modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is one of",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not one of",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];

    useEffect(() => {
        if (!props.modeRestriction) return;
        const currentMode = props.store.containsPrimaryTopics
            ? "include"
            : "exclude";
        if (allowedModes.includes(currentMode)) return;
        props.store.setContainsPrimaryTopics(allowedModes[0] === "include");
    }, [allowedModes, props.modeRestriction, props.store]);

    if (!authStore.canUserView("Topics")) return null;

    return (
        <FilterSectionContainer>
            <Grid id={props.id} container item direction="column">
                <Grid item>
                    <AcxSelectSingle
                        id={"application-filters-primary-topics-contains"}
                        options={modeOptions}
                        labelField="label"
                        valueField="value"
                        defaultValue={{
                            label: props.store.containsPrimaryTopicsString,
                            value: props.store.containsPrimaryTopics,
                        }}
                        customStyle={customFilterSelectStyles}
                        onChange={(item) => {
                            if (item.isDisabled) return;
                            props.store.setContainsPrimaryTopics(item.value);
                        }}
                        isDisabled={modeOptions.filter((item) => !item.isDisabled).length <= 1}
                    />
                </Grid>
                <AcxVirtualizedSelectMulti
                    id={"topics-filter-selector"}
                    defaultSelectedIds={props.store.primaryTopics.map((i) =>
                        i.topicNumber.toString(),
                    )}
                    onApply={(selectedIds: string[]) => {
                        if (!valuesStore.primaryTopics) return;
                        props.store.setPrimaryTopics(
                            availablePrimaryTopics.filter((t) =>
                                selectedIds.includes(t.topicNumber.toString()),
                            ),
                        );
                    }}
                    options={availablePrimaryTopics}
                    isLoading={valuesStore.getTaskLoading(
                        ApplicationFilterValuesStore.Tasks.LOAD_PRIMARY_TOPICS,
                    )}
                    itemName="Topic"
                    labelField="topicLabel"
                    idField="topicNumber"
                    maxSelectionCount={10000}
                    warningMessage={
                        "No more than 10k topics may be selected at once."
                    }
                />
            </Grid>
        </FilterSectionContainer>
    );
});
